import Gardener from 'scoreboard/components/gardener'
import visible from 'scoreboard/scorecards/visible'
import _ from 'lodash'
import Backbone from 'backbone'
import TreeStream from 'scoreboard/components/tree-stream'
import calendars from 'scoreboard/components/calendars'
import globals from 'scoreboard/components/globals'
import { Transform } from 'stream'
import scorecardsPasteAllowed, {locked} from './edit/scorecards-paste-allowed'
import pendingDeletionTransform from 'scoreboard/components/pending-deletion-transform'
import * as AddTypes from 'scoreboard/components/add-types'
const { CHILD, SIBLING } = AddTypes
import unwrapModel from 'scoreboard/components/unwrap-model'
import paste from 'scoreboard/components/paste/paste'
import errorHandler from 'scoreboard/components/error-handler'
import iconForObject from '../components/icon-for-object'

/**
 * Gardener responsible for displaying scorecard trees.
 *
 * @constructs
 *
 * @param {Object} [options.showColors=false] whether score colors should be
 * displayed in the tree
 */
var ScorecardsGardener = Gardener.extend({

	forest: false,

	/**
	 * Gardener responsible for displaying scorecard trees.
	 *
	 * @constructs
	 *
	 *
	 * @param {Object} options
	 *
	 * @param {Object} [options.showColors=false] whether score colors should be
	 * displayed in the tree
	 *
	 * @param {Number} options.periodId the calendar period to render
	 */
	initialize(options = {}) {
		Gardener.prototype.initialize.apply(this, arguments)
		this.periodId = options.periodId
	},

	_bindEvents: function() {
		Gardener.prototype._bindEvents.apply(this, arguments)
		//#21998 the gardener can be reloaded by #21290 if that happens prevent multiple listener being registered
		if (this._scorecardGardenerEventsBound) {
			return
		}
		this.listenTo(Backbone, 'change:period', this.changePeriod)

		// If the root node is hidden, trigger the nodeVisibility event.
		// Wait till the tree is rendered before checking.
		this.on('change:tree', function () {
			process.nextTick(function() {
				if (this.tree.root && !this.tree.get(this.tree.root.id).visible) {
					this.trigger('change:nodeVisibility')
				}
			}.bind(this))
		}.bind(this))
		this._scorecardGardenerEventsBound = true
	},

	_url: function() {
		let urlPrefix = Gardener.prototype._url.apply(this, arguments)
		if (_.isEmpty(urlPrefix)) {
			return null // we don't know what tree to display yet so return null
		}
		if (this.options.showColors) {
			return urlPrefix + '/' + globals.periodId
		} else {
			return urlPrefix
		}
	},

	_treeOptions: function() {
		var treeOptions = Gardener.prototype._treeOptions.apply(this, arguments)

		treeOptions.accessors = {
			icon: 'icon',
			syncId: 'syncId',
			originallyDocked: 'originallyDocked',
			dereferenced: 'dereferenced'
		}
		treeOptions.indicator = _.isUndefined(this.options.indicator) ?
			true : this.options.indicator
		treeOptions.droppable = this.droppable.bind(this)

		if (this.periodId) {
			var period = calendars.periodsById[this.periodId]
			let map = new Transform({ objectMode: true })
			map._transform = function (node, enc, next) {
				node.visible = visible(node, period)
				this.push(node)
				next()
			}
			treeOptions.stream = treeOptions.stream.pipe(map)
		}

		// #59522 add an "icon" property to each node in the tree
		let map = new Transform({ objectMode: true })
		map._transform = function (node, enc, next) {
			// #59522 fix the icon for the scorecard root
			node.icon = node.type == 'root' ? 'scorecard' : node.type
			this.push(node)
			next()
		}
		treeOptions.stream = treeOptions.stream.pipe(map)

		treeOptions.stream = treeOptions.stream.pipe(
			pendingDeletionTransform(() => this.tree))


		return treeOptions
	},

	/**
	 * Returns the correct icon for a scorecard item based on its type
	 * @param {Object|ScorecardNode} node the node to get the icon for
	 */
	getIcon: function(node) {
		node = unwrapModel(node)
		return node.type == 'root' ? 'scorecard' : node.type
	},

	droppable: function(item, parent, emptyOrg, mode) {
		if (locked(item, this.tree)) {
			return false
		}

		return scorecardsPasteAllowed.apply(null, arguments)
	},

	changePeriod: function(periodId) {
		this.periodId = periodId
		// pull ID from root node, in case the tree was originally constructed with an org ID
		var id = _.get(this.tree.get(), 'id')
		var url = 'api/scorecards/' + id + '/tree-colors/' + periodId

		var stream = new TreeStream({ url })
		// patch the colors that the server sent us
		this.tree.edit(stream)

		// patch the colors the server DIDN'T send us, because they were gray
		var ids = []
		stream.on('data', function(node) {
			// when we get the IDs in the tree from Object.keys they will come
			// through as Strings, so record strings here so we can use _.difference
			ids.push('' + node.id)
		})
		stream.on('end', function() {
			var allTreeIds = Object.keys(this.tree._layout)
			var missingIds = _.difference(allTreeIds, ids)
			var patch = _.map(missingIds, function(id) {
				//the trees are initially loaded with the ids as ints, the kalpa
				//tree code using a strict equalities when checking against the
				//ids, so do not pass a stringify version of the id otherwise
				//things break #27380
				return { id: _.parseInt(id), color: 'gray' }
			})
			this.tree.edit(patch)
		}.bind(this))

		// hide or show nodes based on their start and archive dates
		var period = calendars.periodsById[globals.periodId]
		var patch = []
		var nodesChanged
		for (id in this.tree.nodes) {
			var node = this.tree.nodes[id]
			var v = visible(node, period)
			if (v !== node.visible) {
				node.visible = v
				patch[id] = node
				nodesChanged = true
			}
		}

		this.tree.edit(patch)

		// If there were nodes whose visibility was changed, alert listeners
		if (nodesChanged) {
			this.trigger('change:nodeVisibility')
		}
	},

	_addDomainObject: function(model, relativeId) {
		// Don't change the color of added scorecard briefing slides #26106
		if (model.get('type') == 'briefing') return

		model.set('color', model.get('color') || 'gray')
		// #59522 fix the icon for the scorecard root
		model.set('icon', this.getIcon(model))
		Gardener.prototype._addDomainObject.call(this, model, relativeId)
	},

	_editDomainObject: function(model) {
		if (!this.interested(model)) return

		var node = _.clone(model.attributes)
		var period = calendars.periodsById[globals.periodId]
		node.visible = visible(node, period)
		this._applyTreeEdit(node)
	},

	_deleteDomainObject (model) {
		if (!this.interested(model)) return
		if (model.transformedDocking()) {
			//deleting a transform docking, only deletes the descendants of that node
			_.each(this.tree.children(model), (node) => this.tree.removeNode(node))
		} else {
			Gardener.prototype._deleteDomainObject.apply(this, arguments)
		}
	},

	getAddType: function(relative, newItem) {
		relative = unwrapModel(relative)
		// #8389 can't put linked anything under linked nodes
		if (relative.type === 'linked') {
			return SIBLING
		}
		// #7476 rule 4: If the selected item is a scorecard item, the new item
		// is a child.
		if (_.isNil(newItem)) {
			return CHILD
		}
		// #7476 rule 4: If the selected item is a scorecard item, the new item
		// is a child. #31303 unless the move destination selected was invalid
		let allowed = scorecardsPasteAllowed(newItem, relative)
		return allowed ? CHILD : SIBLING
	},

	/** @override */
	move: function(node, newParent, oldParent, newIndex, oldIndex) {
		let pasteArgs = {
			mode: 'move',
			section: 'scorecards',
			id: node.id,
			parentId: newParent && newParent.id,
			orgId: globals.orgId,
			oldOrgId: globals.orgId,
			index: newIndex
		}

		let options = _.extend({}, pasteArgs, {
			// #44966 if there is an error, undo the move and display the error
			error:  xhr => {
				// return item to its previous position
				this.tree.move(node.id, oldParent.id, oldIndex)
				errorHandler(xhr)
			}
		})

		paste(options, function(ids) {
			// if the root of the tree changed, fire an event
			if (oldIndex == 0 || newIndex == 0) {
				Backbone.trigger('change:root', options, ids)
			}
		})
	}

})

export default ScorecardsGardener

ScorecardsGardener.__filename__ = 'scorecards/scorecards-gardener.js'
